import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import SideMenu from '../components/SideMenu';
import { useTranslation } from 'react-i18next';
import { useWebhooks } from '../context/OutgoingWebhooksContext';
import countries from 'i18n-iso-countries';
import enCountries from 'i18n-iso-countries/langs/en.json';
import './webhooks.css';

countries.registerLocale(enCountries);

function ExpandableTableText({ text, theme, mono = false }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const el = textRef.current;
    if (!el || expanded) {
      return undefined;
    }
    const checkOverflow = () => {
      setOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [text, expanded]);

  return (
    <>
      <div
        ref={textRef}
        className={[
          'webhooks-clampText',
          mono ? 'webhooks-clampText--mono' : '',
          expanded ? 'is-expanded' : '',
        ].join(' ')}
      >
        {text}
      </div>
      {(overflows || expanded) && (
        <button
          type="button"
          className={['webhooks-cellToggle', theme].join(' ')}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? t('showLess') : t('showMore')}
        </button>
      )}
    </>
  );
}

export default function OutgoingWebhooks() {
  const location = useLocation();
  const { currentTheme, theme } = useTheme();
  const { t } = useTranslation();
  const {
    subscriptions,
    refreshSubscriptions,
    createSubscription,
    updateSubscription,
    deleteSubscription,
    loading,
    saving,
    error,
    deliveries,
    refreshOutgoingWebhooks,
    deliveriesLoading,
    deliveriesError,
  } = useWebhooks();
  const [editing, setEditing] = useState(null);
  const [editMode, setEditMode] = useState('edit'); // 'create' | 'edit'
  const [showSecret, setShowSecret] = useState(false);
  const [form, setForm] = useState({
    url: '',
    event_types_text: '',
    event_types: [],
    retry_sequence_text: '',
    retrySequenceMinutes: [],
    status: 'active',
    secret: '',
  });
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [deliveryPageSize, setDeliveryPageSize] = useState(10);
  const [deliveryPage, setDeliveryPage] = useState(1);

  useEffect(() => {
    refreshSubscriptions();
  }, [refreshSubscriptions]);

  useEffect(() => {
    refreshOutgoingWebhooks?.();
  }, [refreshOutgoingWebhooks]);

  const cards = useMemo(() => {
    return subscriptions.map((sub, idx) => {
      const url = sub?.url || '';
      const events = sub?.event_types || [];
      const retrySequenceMinutes = sub?.retry_sequence_minutes || [];
      const secret = sub?.secret || '';
      const status = sub?.status || (sub?.enabled === false ? 'paused' : 'active');
      const createdRaw = sub?.created || sub?.created_at || sub?.createdAt || sub?.createdOn || sub?.created_on || null;
      const id = sub?.id || sub?.subscription_id || `${idx}`;
      return { id, url, events: Array.isArray(events) ? events : [], retrySequenceMinutes, secret, status, createdRaw, raw: sub };
    });
  }, [subscriptions]);

  const formatCreated = (value) => {
    if (!value) return '—';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    try {
      return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
    } catch {
      return d.toLocaleString();
    }
  };

  const deliveryRows = useMemo(() => {
    return (Array.isArray(deliveries) ? deliveries : []).map((row, idx) => ({
      id: row?.id ?? `${idx}`,
      subscriptionId: row?.subscription_id ?? row?.subscriptionId ?? '—',
      provider: row?.provider ?? '—',
      country: row?.country,
      status: row?.status ?? '—',
      timestamp: row?.timestamp,
      tryCount: row?.try_count ?? row?.tryCount ?? '—',
      nextRetryAt: row?.next_retry_at ?? row?.nextRetryAt ?? null,
      errorMessage: row?.error_message ?? row?.errorMessage ?? null,
      payload: row?.payload,
    }));
  }, [deliveries]);

  const deliveryTotal = deliveryRows.length;
  const deliveryTotalPages = Math.max(1, Math.ceil(deliveryTotal / deliveryPageSize));
  const safeDeliveryPage = Math.min(Math.max(1, deliveryPage), deliveryTotalPages);
  const deliveryPageItems = useMemo(() => {
    const start = (safeDeliveryPage - 1) * deliveryPageSize;
    return deliveryRows.slice(start, start + deliveryPageSize);
  }, [deliveryRows, safeDeliveryPage, deliveryPageSize]);

  const toAlpha2 = (numeric) => {
    if (numeric == null) return null;
    const n = String(numeric).padStart(3, '0');
    try {
      return countries.numericToAlpha2(n);
    } catch {
      return null;
    }
  };

  const formatPayloadText = (payload) => {
    if (payload == null) return null;
    if (typeof payload === 'string') return payload;
    try {
      return JSON.stringify(payload, null, 2);
    } catch {
      return String(payload);
    }
  };

  const onRefreshDeliveries = async () => {
    setDeliveryPage(1);
    const fromIso = from ? new Date(from).toISOString() : undefined;
    const toIso = to ? new Date(to).toISOString() : undefined;
    await refreshOutgoingWebhooks?.({ from: fromIso, to: toIso });
  };

  const parseEventTypes = (text) => {
    return String(text || '')
      .split(/[\r\n;,]+/g)
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const parseRetrySequence = (text) => {
    return parseEventTypes(text).map((s) => String(s));
  };

  const formatRetrySequence = (minutes) => {
    const list = Array.isArray(minutes) ? minutes : [];
    if (list.length === 0) return null;
    return list.map((m) => String(m)).join(', ');
  };

  const openEdit = (card) => {
    setEditMode('edit');
    setEditing(card);
    setShowSecret(false);
    setForm({
      url: card.url || '',
      event_types_text: Array.isArray(card.events) ? card.events.join('\n') : '',
      event_types: Array.isArray(card.events) ? card.events : [],
      status: card.status || 'active',
      retry_sequence_text: Array.isArray(card.retrySequenceMinutes)
        ? card.retrySequenceMinutes.map((m) => String(m)).join('\n')
        : '',
      retrySequenceMinutes: card.retrySequenceMinutes || [],
      secret: card.secret || '',
    });
  };

  const openCreate = () => {
    setEditMode('create');
    setEditing({ id: null });
    setShowSecret(false);
    setForm({
      url: '',
      event_types_text: '',
      event_types: [],
      retry_sequence_text: '',
      retrySequenceMinutes: [],
      status: 'active',
      secret: '',
    });
  };
  useEffect(() => { if (location.state?.quickCreate) openCreate(); }, [location.state]);

  const closeEdit = () => {
    setEditing(null);
  };

  const onSave = async () => {
    const payload = {
      id: editing?.id || undefined,
      url: form.url,
      event_types: Array.isArray(form.event_types) ? form.event_types : parseEventTypes(form.event_types_text),
      status: form.status,
      retry_sequence_minutes: parseRetrySequence(form.retry_sequence_text),
      secret: form.secret || '',
    };

    const res = editMode === 'create'
      ? await createSubscription?.(payload)
      : await updateSubscription?.(editing.id, payload);
    if (res?.ok) {
      closeEdit();
    }
  };

  const onRemove = async () => {
    if (editMode !== 'edit') return;
    if (!editing?.id) return;

    const ok = window.confirm(t('confirmRemoveSubscription'));
    if (!ok) return;

    const res = await deleteSubscription?.(editing.id);
    if (res?.ok) {
      closeEdit();
    }
  };

  return (
    <div
      className="webhooks-page"
      style={{
        '--wh-background': currentTheme.background,
        '--wh-surface': currentTheme.surface,
        '--wh-border': currentTheme.border,
        '--wh-card-shadow': currentTheme.cardShadow,
        '--wh-text': currentTheme.text,
        '--wh-text-secondary': currentTheme.textSecondary,
      }}
    >
      <SideMenu currentTheme={currentTheme} theme={theme} />

      <main
        className="webhooks-main"
      >
        <h1 className="webhooks-title">{t('outgoingWebhooks')}</h1>

        {loading && (
          <div className="webhooks-muted">
            {t('Loading')}...
          </div>
        )}

        {!loading && error && (
          <div
            className={`webhooks-error ${theme}`}
          >
            {t(error)}
          </div>
        )}

        {!loading && (
          <div className="webhooks-tableWrap" style={{ marginBottom: 16 }}>
            <div
              className={`webhooks-tableTopbar ${theme}`}
            >
              <div className="webhooks-tableTopbarTitle">
                {t('Subscriptions')}
              </div>
              <button
                type="button"
                onClick={openCreate}
                disabled={saving}
                className={[
                  'webhooks-primaryBtn',
                  theme,
                  saving ? 'disabled' : 'enabled'
                ].join(' ')}
              >
                {t('Add webhook')}
              </button>
            </div>
            {cards.length > 0 ? (
            <table className="webhooks-table">
              <thead>
                <tr>
                <th
                    className="webhooks-th"
                  >
                    {t('Id')}
                  </th>
                  <th
                    className="webhooks-th"
                  >
                    {t('URL')}
                  </th>
                  <th
                    className="webhooks-th"
                  >
                    {t('Created')}
                  </th>
                  <th
                    className="webhooks-th"
                  >
                    {t('Status')}
                  </th>
                  <th
                    className="webhooks-th"
                  >
                    {t('retrySequence')}
                  </th>
                  <th
                    className="webhooks-th"
                  >
                    {t('Events')}
                  </th>
                  <th
                    className="webhooks-th webhooks-thRight"
                  >
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {cards.map((sub, idx) => (
                  <tr key={sub.id} className={idx === cards.length - 1 ? 'webhooks-trLast' : ''}>
                     <td className="webhooks-td">
                      <div className="webhooks-urlCell">
                        {sub.id}
                      </div>
                    </td>
                    <td className="webhooks-td">
                      <div className="webhooks-urlCell">
                        {sub.url || t('Webhook')}
                      </div>
                    </td>
                    <td className="webhooks-td webhooks-createdCell" title={sub.createdRaw ? String(sub.createdRaw) : undefined}>
                      {formatCreated(sub.createdRaw)}
                    </td>
                    <td className="webhooks-td">
                      <span className={['webhooks-statusPill', sub.status === 'active' ? 'active' : 'disabled', theme].join(' ')}>
                        {sub.status}
                      </span>
                    </td>
                    <td className="webhooks-td">
                      {formatRetrySequence(sub.retrySequenceMinutes) ? (
                        <span className="webhooks-createdCell">{formatRetrySequence(sub.retrySequenceMinutes)}</span>
                      ) : (
                        <span className="webhooks-muted">—</span>
                      )}
                    </td>
                    <td className="webhooks-td">
                      {sub.events.length > 0 ? (
                        <div className="webhooks-events">
                          {sub.events.map((evt) => (
                            <span
                              key={evt}
                              className={['webhooks-eventChip', theme].join(' ')}
                            >
                              {evt}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="webhooks-muted">—</span>
                      )}
                    </td>
                    <td className="webhooks-td webhooks-actionsCell">
                      <button
                        type="button"
                        onClick={() => openEdit(sub)}
                        aria-label={t('EditSubscription')}
                        title={t('EditSubscription')}
                        className={['webhooks-iconBtn', theme].join(' ')}
                      >
                        <span className="oi oi-ellipses" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            ) : (
              <div className="webhooks-muted webhooks-tableEmpty">
                {t('No webhook subscriptions yet')}
              </div>
            )}
          </div>
        )}

        <div className={['webhooks-filters', 'webhooks-sectionGap'].join(' ')}>
          <label className="webhooks-filterLabel">
            <div className="webhooks-fieldLabel">{t('From')}</div>
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setDeliveryPage(1);
              }}
              className={['webhooks-filterInput', theme].join(' ')}
            />
          </label>
          <label className="webhooks-filterLabel">
            <div className="webhooks-fieldLabel">{t('To')}</div>
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setDeliveryPage(1);
              }}
              className={['webhooks-filterInput', theme].join(' ')}
            />
          </label>
          <button
            type="button"
            onClick={onRefreshDeliveries}
            disabled={deliveriesLoading}
            className={[
              'webhooks-primaryBtn',
              theme,
              deliveriesLoading ? 'disabled' : 'enabled',
            ].join(' ')}
            style={{ marginLeft: 'auto' }}
          >
            {t('refresh')}
          </button>
        </div>

        {deliveriesLoading && (
          <div className="webhooks-muted webhooks-sectionGap">
            {t('loading')}
          </div>
        )}

        {!deliveriesLoading && deliveriesError && (
          <div className={['webhooks-error', 'webhooks-sectionGap', theme].join(' ')}>
            {deliveriesError}
          </div>
        )}

        {!deliveriesLoading && !deliveriesError && (
          <div className={['webhooks-tableWrap', 'webhooks-sectionGap'].join(' ')}>
            <div className={`webhooks-tableTopbar ${theme}`}>
              <div className="webhooks-tableTopbarTitle">
                {t('webhookDelivery')}: {deliveryTotal}
              </div>
              <div className="webhooks-paginationBar">
                <div className="webhooks-fieldLabel">{t('Items per page')}</div>
                <select
                  value={deliveryPageSize}
                  onChange={(e) => {
                    setDeliveryPageSize(Number(e.target.value));
                    setDeliveryPage(1);
                  }}
                  className={['webhooks-select', theme].join(' ')}
                  style={{
                    width: 'auto',
                    minWidth: 72,
                    colorScheme: theme === 'dark' ? 'dark' : 'light',
                  }}
                >
                  <option value={10}>10</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <button
                  type="button"
                  disabled={safeDeliveryPage <= 1}
                  onClick={() => setDeliveryPage((p) => Math.max(1, p - 1))}
                  className={['webhooks-secondaryBtn', theme, safeDeliveryPage <= 1 ? 'disabled' : 'enabled'].join(' ')}
                >
                  {t('Prev')}
                </button>
                <div className="webhooks-muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                  {t('Page')} {safeDeliveryPage} {t('of')} {deliveryTotalPages}
                </div>
                <button
                  type="button"
                  disabled={safeDeliveryPage >= deliveryTotalPages}
                  onClick={() => setDeliveryPage((p) => Math.min(deliveryTotalPages, p + 1))}
                  className={[
                    'webhooks-secondaryBtn',
                    theme,
                    safeDeliveryPage >= deliveryTotalPages ? 'disabled' : 'enabled',
                  ].join(' ')}
                >
                  {t('Next')}
                </button>
              </div>
            </div>
            {deliveryPageItems.length > 0 ? (
              <table className="webhooks-table webhooks-table--delivery">
                <thead>
                  <tr>
                    <th className="webhooks-th">{t('Timestamp')}</th>
                    <th className="webhooks-th">{t('Provider')}</th>
                    <th className="webhooks-th">{t('country')}</th>
                    <th className="webhooks-th">{t('subscriptionId')}</th>
                    <th className="webhooks-th">{t('Status')}</th>
                    <th className="webhooks-th">{t('retryCount')}</th>
                    <th className="webhooks-th">{t('nextRetryAt')}</th>
                    <th className="webhooks-th webhooks-errorCell">{t('errorMessage')}</th>
                    <th className="webhooks-th webhooks-payloadCell">{t('Payload')}</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveryPageItems.map((row, idx) => {
                    const alpha2 = toAlpha2(row.country);
                    const code = alpha2 ? String(alpha2).toLowerCase() : '';
                    const isDelivered = row.status === 'delivered';
                    return (
                      <tr key={row.id} className={idx === deliveryPageItems.length - 1 ? 'webhooks-trLast' : ''}>
                        <td className="webhooks-td webhooks-createdCell" title={row.timestamp ? String(row.timestamp) : undefined}>
                          {formatCreated(row.timestamp)}
                        </td>
                        <td className="webhooks-td">{row.provider}</td>
                        <td className="webhooks-td">
                          {alpha2 ? (
                            <span className={`fi fi-${code}`} aria-hidden="true" title={`${alpha2} (${row.country})`} />
                          ) : (
                            <span className="webhooks-muted">—</span>
                          )}
                        </td>
                        <td className="webhooks-td">
                          <div className="webhooks-urlCell">{row.subscriptionId}</div>
                        </td>
                        <td className="webhooks-td">
                          <span
                            className={[
                              'webhooks-statusPill',
                              isDelivered ? 'active' : 'disabled',
                              theme,
                            ].join(' ')}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td className="webhooks-td">{row.tryCount}</td>
                        <td className="webhooks-td webhooks-createdCell">
                          {formatCreated(row.nextRetryAt)}
                        </td>
                        <td className="webhooks-td webhooks-errorCell">
                          {row.errorMessage ? (
                            <ExpandableTableText text={row.errorMessage} theme={theme} />
                          ) : (
                            <span className="webhooks-muted">—</span>
                          )}
                        </td>
                        <td className="webhooks-td webhooks-payloadCell">
                          {(() => {
                            const payloadText = formatPayloadText(row.payload);
                            return payloadText ? (
                              <ExpandableTableText text={payloadText} theme={theme} mono />
                            ) : (
                            <span className="webhooks-muted">—</span>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="webhooks-muted webhooks-tableEmpty">
                {t('noOutgoingWebhooks')}
              </div>
            )}
          </div>
        )}

        {!!editing && (
          <div
            role="dialog"
            aria-modal="true"
            className={['webhooks-modalOverlay', theme].join(' ')}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeEdit();
            }}
          >
            <div className="webhooks-modal">
              <div className="webhooks-modalHeader">
                <div className="webhooks-modalTitle">
                  {editMode === 'create' ? t('Add webhook') : t('Edit subscription')}
                </div>
                <button
                  type="button"
                  onClick={closeEdit}
                  className={['webhooks-iconBtn', theme].join(' ')}
                  aria-label={t('Cancel')}
                  title={t('Cancel')}
                >
                  ×
                </button>
              </div>

              <div className="webhooks-modalBody">
              <div className="webhooks-formGrid">
                <label className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('URL')}</div>
                  <input
                    value={form.url}
                    onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                    className={['webhooks-input', theme].join(' ')}
                  />
                </label>

                <label className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('Secret')}</div>
                  <div className="webhooks-secretWrap">
                    <input
                      type={showSecret ? 'text' : 'password'}
                      value={form.secret || ''}
                      onChange={(e) => setForm((f) => ({ ...f, secret: e.target.value }))}
                      className={['webhooks-input', 'webhooks-secretInput', theme].join(' ')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecret((v) => !v)}
                      className={['webhooks-iconBtn', 'webhooks-secretToggle', theme].join(' ')}
                      aria-label={showSecret ? t('HideSecret') : t('ShowSecret')}
                      title={showSecret ? t('HideSecret') : t('ShowSecret')}
                    >
                      <span className="oi oi-eye" aria-hidden="true" />
                    </button>
                  </div>
                </label>

                <label className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('Events')}</div>
                  <textarea
                    value={form.event_types_text}
                    onChange={(e) => {
                      const text = e.target.value;
                      setForm((f) => ({ ...f, event_types_text: text, event_types: parseEventTypes(text) }));
                    }}
                    rows={6}
                    placeholder={t('One per line or comma-separated')}
                    className={['webhooks-textarea', theme].join(' ')}
                  />
                </label>

                <label className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('retrySequence')}</div>
                  <textarea
                    value={form.retry_sequence_text}
                    onChange={(e) => {
                      const text = e.target.value;
                      setForm((f) => ({
                        ...f,
                        retry_sequence_text: text,
                        retrySequenceMinutes: parseRetrySequence(text),
                      }));
                    }}
                    rows={4}
                    placeholder={t('retrySequenceHint')}
                    className={['webhooks-textarea', theme].join(' ')}
                  />
                </label>

                <label className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('Status')}</div>
                  <select
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    className={['webhooks-select', theme].join(' ')}
                    style={{
                      backgroundColor: currentTheme.surface,
                      color: currentTheme.text,
                      borderColor: currentTheme.border,
                      colorScheme: theme === 'dark' ? 'dark' : 'light',
                    }}
                  >
                    <option value="active">{t('Active')}</option>
                    <option value="paused">{t('Paused')}</option>
                  </select>
                </label>
              </div>
              </div>

              <div className="webhooks-modalFooter">
                {editMode === 'edit' && (
                  <button
                    type="button"
                    onClick={onRemove}
                    disabled={saving}
                    className={[
                      'webhooks-dangerBtn',
                      theme,
                      saving ? 'disabled' : 'enabled'
                    ].join(' ')}
                  >
                    {t('Remove')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeEdit}
                  disabled={saving}
                  className={[
                    'webhooks-secondaryBtn',
                    theme,
                    saving ? 'disabled' : 'enabled'
                  ].join(' ')}
                >
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  onClick={onSave}
                  disabled={saving}
                  className={[
                    'webhooks-primaryBtn',
                    theme,
                    saving ? 'disabled' : 'enabled'
                  ].join(' ')}
                >
                  {saving ? t('Saving') + '...' : t('save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
